import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Parallax from "./Parallax";

const MODELS = [
  {
    code: "EOR",
    name: "Employer of Record",
    desc: "For an initial hire or early market exploration where a local employment arrangement is needed.",
    points: ["Compliant local employment for individual hires", "Payroll and statutory administration", "Suitable while you test the market or a single role"],
  },
  {
    code: "ODC",
    name: "Dedicated Offshore Development Centre",
    desc: "For a dedicated team working on your priorities and connected to your operating rhythms.",
    points: ["A team focused solely on your business", "Aligned with your workflows, tools, and ways of working", "Suitable for engineering, design, operations, and more"],
  },
  {
    code: "CoE",
    name: "Centre of Excellence",
    desc: "For building specialised expertise, repeatable practices, or a focused capability in India.",
    points: ["A concentrated team around a single capability", "Standards, playbooks, and shared craft", "Scales expertise beyond a single project"],
  },
  {
    code: "GCC",
    name: "Global Capability Centre",
    desc: "For a more strategically integrated, long-term India operation aligned with your wider organisation.",
    points: ["Multi-function India presence over time", "Deeper integration with your global operating model", "Suited to organisations planning for the long term"],
  },
];

export default function Models() {
  return (
    <section id="models" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-[#EDE6FC]" data-testid="models-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14"
        >
          <div className="max-w-2xl">
            <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
              Engagement models
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#0B132B] leading-[1.04]">
              The right structure for your next stage.
            </h2>
          </div>
          <p className="text-[#475569] text-base max-w-sm lg:text-right leading-relaxed">
            Choose the model that fits your objectives, operating needs, and level of integration.
            Start where you are and scale as your India operation evolves.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
          {MODELS.map((m, i) => (
            <Parallax key={m.code} distance={i % 2 === 0 ? 34 : -26} className="h-full">
              <motion.article
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`card-jeton group relative h-full rounded-[28px] bg-white shadow-[0_24px_60px_-18px_rgba(76,29,149,0.22)] p-8 sm:p-10 overflow-hidden ${
                  i % 2 === 0 ? "lg:-rotate-1 lg:mt-2" : "lg:rotate-1 lg:-mt-2"
                } hover:rotate-0`}
                data-testid={`model-card-${m.code.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-7">
                  <span className="inline-block font-bold text-xs tracking-[0.15em] rounded-full px-4 py-1.5 bg-[#5B4199] text-white">
                    {m.code}
                  </span>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#5B4199]/30 font-mono-tech text-xs text-[#5B4199]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-[#0B132B] mb-3">
                  {m.name}
                </h3>
                <p className="text-[#475569] text-[15px] leading-relaxed mb-7 max-w-md">{m.desc}</p>
                <ul className="space-y-2.5">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-[#475569]">
                      <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-[#5B4199] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B132B] group-hover:text-[#5B4199] transition-colors duration-300"
                  data-testid={`model-cta-${m.code.toLowerCase()}`}
                >
                  Discuss the right model
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.article>
            </Parallax>
          ))}
        </div>
      </div>
    </section>
  );
}
